import React from 'react'
import { CountryAffectation, SplitAffectation } from '../../../models/Portfolio';
import { useAppSelector } from '../../../store/reducers/store';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, ITheme, Link, ListItemIcon, ListItemText, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import { Country } from '../../../models/Common';
import { LeoImagesCountriesPath } from '../../../resources/api-constants';
import { ReactComponent as otherIcon } from '../../../assets/others.svg';
import ProportionSelector from './ProportionSelector';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

export interface ICountrySelectorProps {
    splits: SplitAffectation[];
    value: CountryAffectation[];
    onCountriesChanged: (sectors: CountryAffectation[]) => void;
}

const CountrySelector: React.FC<ICountrySelectorProps> = ({ onCountriesChanged, value, splits }) => {
    const theme = useTheme() as ITheme;
    const countries = useAppSelector(state => state.createPortfolio.countries);
    const selectedCountries = useAppSelector(state => state.createPortfolio.portfolio.countries);
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    if (countries === undefined) {
        return <CircularProgress />
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const onSplitSelected = (id: number): void => {
        const selection = [...value];
        const existingIndex = selection.findIndex(i => i.id === id);
        if (existingIndex !== -1) {
            selection.splice(existingIndex, 1);
        } else {
            const split = countries.find(s => s.id === id);
            if (split) {
                selection.push({ ...split, proportion: 0 })
            }
        }
        onCountriesChanged(selection);
    }

    const onProportionChanged = (id: number, proportion: number): void => {
        const selection = [...value];
        let total = selection.reduce((a, b) => a + b.proportion, 0);
        const existingIndex = selection.findIndex(i => i.id === id);
        if (existingIndex !== -1) {
            let correctedValue = proportion;
            total -= selection[existingIndex].proportion;
            if (total + correctedValue > 1) {
                correctedValue = 1 - total;
            }
            selection[existingIndex] = { ...selection[existingIndex], proportion: correctedValue };
        }
        onCountriesChanged(selection);
    }

    const countriesToDisplay = splits.flatMap(s => s.countries);
    for (const selected of value) {
        if (countriesToDisplay.findIndex(c => c.id === selected.id) === -1) {
            countriesToDisplay.push(selected);
        }
    }
    const renderCountry = (country: Country, affectation: CountryAffectation | undefined, onClick: (id: number) => void): React.ReactNode => {
        const selected = affectation !== undefined;
        return <Box display={"flex"} justifyContent={"flex-start"} alignItems={"center"} key={`split-${country.id}`}
            bgcolor={selected ? theme.palette.primary.dark: theme.palette.background.paper} color={selected ? theme.palette.common.white: theme.palette.text.primary} borderRadius={"4px"}
            padding={"6px 12px"} gap={"8px"} onClick={() => onClick(country.id)} sx={{ cursor: "pointer", userSelect: "none" }}>
            {country.icon && <img src={`${LeoImagesCountriesPath}/${country.icon}`} className={selected ? "svg-color-white" : ""} style={{ width: "20px", height: "20px", background: theme.palette.background.paper, padding: "0 1px" }}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { e.currentTarget.style.display = 'none'; }} />}
            <Typography fontSize={"13px"} lineHeight={"20px"} fontWeight={"500"} color={selected ? theme.palette.common.white: theme.palette.text.primary}>{country.name}</Typography>
            {/* {selected && <ProportionSelector value={affectation.proportion} onProportionChanged={(val: number) => onProportionChanged(country.id, val)} />} */}
            {selected && <Link sx={{ color: theme.palette.text.primary }}><CloseIcon fontSize='small' sx={{ color: theme.palette.background.paper }} /></Link>}
        </Box>
    }
    return (
        <Box display={"flex"} flexDirection={"column"} gap={"24px"}>
            <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioSelectCountryLabel')}</Typography>
            <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                {countriesToDisplay.map(c => renderCountry(c, value.find(v => v.id === c.id), onSplitSelected))}
                <Box display={"flex"} justifyContent={"flex-start"} alignItems={"center"}
                    bgcolor={theme.palette.background.paper} color={theme.palette.text.primary} borderRadius={"4px"}
                    padding={"6px 12px"} gap={"8px"} onClick={handleClick} sx={{ cursor: "pointer", userSelect: "none" }}>
                    <SvgIcon component={otherIcon} inheritViewBox width={"16"} height={"16"} />
                    <Typography fontSize={"13px"} lineHeight={"20px"} fontWeight={"500"} color={theme.palette.text.primary}>{t('CreatePortfolioSelectCountryOtherTag')}</Typography>
                </Box>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    {countries.filter(c => countriesToDisplay.findIndex(s => s.id === c.id) === -1 && c.name !== "Other").map(c => <MenuItem onClick={() => onSplitSelected(c.id)} key={`country-${c.id}`}>
                        {c.icon && <ListItemIcon>
                            <img src={`${LeoImagesCountriesPath}/${c.icon}`} />
                        </ListItemIcon>}
                        <ListItemText>{c.name}</ListItemText>
                    </MenuItem>)}
                    {/* <MenuItem onClick={handleClose}>Profile</MenuItem>
                    <MenuItem onClick={handleClose}>My account</MenuItem>
                    <MenuItem onClick={handleClose}>Logout</MenuItem> */}
                </Menu>
            </Box>
        </Box>
    )
}

export default CountrySelector